import { UserLatchAPI, shouldTrack } from './api';
import { SessionRecorder } from './recorder';
import type { UserLatchConfig, UserLatchUser, UserLatchInstance } from './types';

function generateSessionId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getSessionId(): string {
  const SESSION_KEY = 'userlatch_session_id';
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

export function createTracker(): UserLatchInstance {
  let api: UserLatchAPI | null = null;
  let currentUser: UserLatchUser | null = null;
  let sessionId: string | null = null;
  let initialized = false;
  let debug = false;
  let recorder: SessionRecorder | null = null;

  function log(...args: unknown[]): void {
    if (debug) {
      console.log('[UserLatch]', ...args);
    }
  }

  function trackPageView(): void {
    if (!initialized || !api) return;
    
    const properties = {
      url: window.location.href,
      path: window.location.pathname,
      referrer: document.referrer,
      title: document.title,
    };

    api.track({
      name: '$pageview',
      properties,
      timestamp: Date.now(),
      sessionId: sessionId!,
    });

    // Record page view for session metrics
    if (recorder) {
      recorder.recordPageView();
    }

    log('Page view tracked:', properties);
  }

  function setupAutoTracking(): void {
    // Track initial page view
    trackPageView();

    // Track navigation in SPAs
    if (typeof window !== 'undefined') {
      const originalPushState = history.pushState;
      history.pushState = function (...args) {
        originalPushState.apply(this, args);
        trackPageView();
      };

      window.addEventListener('popstate', () => {
        trackPageView();
      });
    }
  }

  function setupClickTracking(): void {
    if (typeof document === 'undefined') return;

    // Track all click events
    document.addEventListener('click', (event) => {
      if (!recorder) return;

      // Record click for session metrics
      recorder.recordClick();

      // Get information about the clicked element
      const target = event.target as HTMLElement;
      if (!target) return;

      // Build a simple selector for the element
      const tagName = target.tagName?.toLowerCase() || 'unknown';
      const id = target.id ? `#${target.id}` : '';
      const classes = target.className && typeof target.className === 'string' 
        ? `.${target.className.split(' ').filter(Boolean).join('.')}` 
        : '';
      
      // Get text content (truncated)
      const text = (target.textContent || '').trim().substring(0, 50);

      log('Click recorded:', { element: `${tagName}${id}${classes}`, text });
    }, { capture: true, passive: true });
  }

  async function startSession(): Promise<void> {
    if (!api || !currentUser || !sessionId) return;

    await api.startSession({
      sessionId,
      userEmail: currentUser.email,
      userAgent: navigator.userAgent,
      initialUrl: window.location.href,
    });

    log('Session started:', sessionId);
  }

  async function sendSessionChunk(events: unknown[], clickCount: number, pageViewCount: number): Promise<void> {
    if (!api || !currentUser || !sessionId) return;

    const success = await api.sendSessionChunk({
      sessionId,
      userEmail: currentUser.email,
      events,
      clickCount,
      pageViewCount,
    });

    if (success) {
      log('Session chunk sent:', events.length, 'events,', clickCount, 'clicks');
    }
  }

  async function endSession(): Promise<void> {
    if (!api || !currentUser || !sessionId) return;

    await api.endSession({
      sessionId,
      userEmail: currentUser.email,
    });

    log('Session ended:', sessionId);
  }

  return {
    async init(config: UserLatchConfig): Promise<void> {
      if (initialized) {
        console.warn('[UserLatch] Already initialized');
        return;
      }

      if (!config.apiKey) {
        console.error('[UserLatch] API key is required');
        return;
      }

      if (!config.user?.email) {
        console.error('[UserLatch] User email is required');
        return;
      }

      debug = config.debug ?? false;

      // Check if this user should be tracked (client-side blocklist)
      const canTrack = await shouldTrack(
        config.apiKey,
        config.user.email,
        config.endpoint
      );
      if (!canTrack) {
        log('User is untracked, skipping initialization');
        initialized = true;
        return; // No api/recorder - track/identify/reset will no-op via guards
      }

      api = new UserLatchAPI(config.apiKey, config.endpoint);
      currentUser = config.user;
      sessionId = getSessionId();
      initialized = true;

      log('Initialized with config:', config);

      // Set user email for tracking
      api.setUserEmail(currentUser.email);

      // Identify user
      api.identify(currentUser);

      // Setup auto-tracking (page views)
      setupAutoTracking();

      // Setup click tracking
      setupClickTracking();

      // Setup session recording if enabled
      const recordingConfig = config.recording;
      if (recordingConfig?.enabled !== false) {
        recorder = new SessionRecorder({
          uploadIntervalSeconds: 30, // Upload every 30 seconds
          maskAllInputs: recordingConfig?.maskAllInputs ?? false,
          maskSelectors: recordingConfig?.maskSelectors ?? [],
          onChunkReady: (events, metrics) => {
            sendSessionChunk(events, metrics.clickCount, metrics.pageViewCount);
          },
          onStop: (events, metrics) => {
            // Send final chunk
            sendSessionChunk(events, metrics.clickCount, metrics.pageViewCount);
          },
        });

        // Start the session on the server
        startSession();

        // Start recording
        recorder.start();
        log('Session recording started');

        // Handle page unload - send final data and end session
        window.addEventListener('beforeunload', () => {
          if (recorder) {
            // Force flush any remaining events
            const remaining = recorder.forceFlush();
            if (remaining && remaining.events.length > 0) {
              // Use synchronous approach for reliability
              const payload = {
                action: 'chunk',
                payload: {
                  sessionId,
                  userEmail: currentUser?.email,
                  events: remaining.events,
                  clickCount: remaining.metrics.clickCount,
                  pageViewCount: remaining.metrics.pageViewCount,
                },
              };

              // Try to send via fetch with keepalive
              try {
                fetch(`${config.endpoint || 'https://curious-hornet-755.convex.site'}/session`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-UserLatch-Key': config.apiKey,
                  },
                  body: JSON.stringify(payload),
                  keepalive: true,
                });
              } catch {
                // Ignore errors during unload
              }
            }

            // End the session
            try {
              fetch(`${config.endpoint || 'https://curious-hornet-755.convex.site'}/session`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-UserLatch-Key': config.apiKey,
                },
                body: JSON.stringify({
                  action: 'end',
                  payload: {
                    sessionId,
                    userEmail: currentUser?.email,
                  },
                }),
                keepalive: true,
              });
            } catch {
              // Ignore errors during unload
            }
          }
        });

        // Also handle visibility change (mobile browsers)
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden' && recorder) {
            const remaining = recorder.forceFlush();
            if (remaining && remaining.events.length > 0) {
              sendSessionChunk(remaining.events, remaining.metrics.clickCount, remaining.metrics.pageViewCount);
            }
          }
        });
      }
    },

    track(eventName: string, properties?: Record<string, unknown>): void {
      if (!api) return; // No-op when untracked or not initialized
      if (!initialized) {
        console.warn('[UserLatch] Not initialized. Call userLatch.init() first.');
        return;
      }

      api.track({
        name: eventName,
        properties,
        timestamp: Date.now(),
        sessionId: sessionId!,
      });

      log('Event tracked:', eventName, properties);
    },

    identify(user: UserLatchUser): void {
      if (!api) return; // No-op when untracked or not initialized
      if (!initialized) {
        console.warn('[UserLatch] Not initialized. Call userLatch.init() first.');
        return;
      }

      if (!user.email) {
        console.error('[UserLatch] User email is required');
        return;
      }

      currentUser = { ...currentUser, ...user };
      api.identify(currentUser);

      log('User identified:', currentUser);
    },

    reset(): void {
      if (recorder) {
        recorder.stop();
        recorder = null;
      }
      if (api) {
        api.destroy();
      }
      api = null;
      currentUser = null;
      sessionId = null;
      initialized = false;
      sessionStorage.removeItem('userlatch_session_id');

      log('Reset complete');
    },
  };
}
